/**
 * §8 — the Case File: the composed panel note, the case head grid, the five-step stepper,
 * and as much of step card 01 as the case record honestly supports.
 *
 * **What is here.** §8's six-cell head grid, the stepper and its state machine, the 24-hour
 * rule banner (both branches — the record carries `isLateReport` and `lateReasons`), the
 * `Case Created` fields, and the charge boxes with the article wording quoted as it was bound
 * to this tournament and the minimum penalty that level carries.
 *
 * **What is deliberately absent, and says so.** §8's step cards 02 to 05 are mostly reads of
 * records nothing writes yet, and each is named in the not-built note rather than drawn:
 *
 *   · the **natural-justice checklist** of card 02 — it is the compliance gate on a letter,
 *     and there is no letter;
 *   · the **attendees** of card 03 and the **in-absentia** inserts they qualify;
 *   · the **decision record** of card 04 — accepts-the-breach, disciplinary history, attitude,
 *     penalty already incurred, finding, reasoning and the grading sentence;
 *   · the **penalty** of card 05, and with it §13's whole sanction tracker;
 *   · the **evidence files** — §8's three slots — and the **internal notes** textarea, which
 *     is read-only here because there is no write route for it.
 *
 * A drawn-but-empty card would read as a case with nothing in it. A named absence reads as a
 * feature that is not built, which is what it is.
 *
 * **§14b's arrangements are now here, with the one control that moves them.** The hearing's
 * time and place, `Amend Hearing Details`, and the Amendment History. Before 22 September 2026
 * this panel showed the hearing's time only inside step 03's caption and its place nowhere at
 * all — so the Technical Delegate who had moved a hearing, and who had therefore retyped the
 * letter by hand, could not see on the case he was looking at what the record still believed.
 * The control is disabled with its reason once the hearing has been held, because migration
 * 030's rule is that the hearing record is editable exactly while its minutes are.
 *
 * **§15's first clock is now here.** The 24-hour report window sits beside the late-report
 * banner it measures, from `deriveLateReportWindow` and Configuration §05's threshold — and
 * where that threshold cannot be read the chip is absent rather than drawn against an assumed
 * 24. §15's second clock, the appeal window, is NOT here: it runs from the Decision Letter's
 * service and belongs on that letter's card, in the Documents panel.
 */

import {
  HEARING_DOCUMENT_STATE_LABELS,
  HEARING_LEVEL_MINIMUM_PENALTY,
  HEARING_STAGE_LABELS,
  HEARING_STAGE_TOOLTIPS,
  type HearingDocumentState,
  type HearingDocumentStates,
} from "@hockeytd/shared";

import { CountryFlag } from "../../app/CountryFlag.js";
import type { HearingCaseDetailView, HearingClockSettings } from "../../api/types.js";
import {
  AMEND_HISTORY_HEADING,
  AMEND_TITLE,
  amendDisabledReason,
  amendmentHistoryLine,
  amendmentLetterNoteOnLine,
} from "./amendmentText.js";
import { LateReportChip } from "./AppealClock.js";
import { TeamManagerField } from "./TeamManagerField.js";
import {
  HEARING_STEP_DESCRIPTORS,
  HEARING_STEP_TITLES,
  caseFileNoteParts,
  caseLevel,
  formatInstant,
  matchRefText,
  minimumPenaltyTitle,
  personRoleShort,
  stepStates,
  type StepState,
} from "./caseText.js";

/** §8's `Time Of The Match` cell: the phase, and the clock time when one was recorded. */
export function matchTimeText(hearing: HearingCaseDetailView): string {
  if (hearing.matchMinute === null) return hearing.matchPhase;
  return `${hearing.matchPhase} · ${String(hearing.matchMinute)}′`;
}

/**
 * §8's per-step captions.
 *
 * Steps 01, 02 and 04 compose from the record. Step 03's caption in the spec is
 * `{date}, {from}` once the hearing has been held — and `from`/`to` are typed into §11's
 * minutes form, which is not built — so a held case says the minutes are filed and that the
 * time range was not recorded, rather than reprinting the SCHEDULED time as though it were
 * the time the hearing ran. Step 05 has no record at all.
 */
export function stepCaptions(
  hearing: HearingCaseDetailView,
  documents: HearingDocumentStates,
): readonly string[] {
  const letter = documents[1];
  const minutes = documents[2];
  const decision = documents[3];
  const scheduled = formatInstant(hearing.hearingAt);

  const held =
    minutes === "not_required"
      ? HEARING_DOCUMENT_STATE_LABELS.not_required
      : minutes === "filed"
        ? "Minutes Filed · Held Time Not Recorded"
        : scheduled === null
          ? "Hearing Time Not Set"
          : `Set For ${scheduled}`;

  const decided =
    decision === "issued" || decision === "not_required"
      ? HEARING_DOCUMENT_STATE_LABELS[decision]
      : "Outstanding";

  return [
    formatInstant(hearing.createdAt) ?? "—",
    HEARING_DOCUMENT_STATE_LABELS[letter],
    held,
    decided,
    "Not Built",
  ];
}

function StepList({ hearing }: { hearing: HearingCaseDetailView }): React.JSX.Element {
  const states: readonly StepState[] = stepStates(hearing.stage);
  const captions = stepCaptions(hearing, hearing.documents);

  return (
    <ol className="stepper" data-testid="stepper">
      {HEARING_STEP_TITLES.map((title, index) => (
        <li
          key={title}
          className={`step step--${states[index] ?? "todo"}`}
          data-testid={`step-${String(index + 1).padStart(2, "0")}`}
          data-state={states[index] ?? "todo"}
        >
          <span className="step__n">{String(index + 1).padStart(2, "0")}</span>
          <span className="step__title">{title}</span>
          <span className="step__cap">{captions[index]}</span>
          <span className="step__desc">{HEARING_STEP_DESCRIPTORS[index]}</span>
        </li>
      ))}
    </ol>
  );
}

export interface CaseFilePanelProps {
  /** The opened case, or `null` before one is chosen — §18's pre-selection placeholder. */
  hearing: HearingCaseDetailView | null;
  /**
   * Configuration §05's thresholds, or null when they could not be read.
   *
   * §15's 24-hour window is a Configuration item and not a constant, because a tournament may
   * run a different one — so where the number cannot be read the chip is absent rather than
   * drawn against an assumed 24.
   */
  clocks?: HearingClockSettings | null;
  /**
   * Bumped after the Team Manager has been named on this case, so the page re-reads it.
   *
   * The Case File is otherwise a pure read, and this is the one write it offers: §10's
   * addressee, on the case that has none, because a case created before the create form
   * asked for one must be fixable without being recreated — a new case number on a
   * disciplinary register is not a correction.
   */
  onChanged?: () => void;
  /**
   * §14b — open `AmendHearingModal` on this case.
   *
   * Undefined where the panel is drawn read-only, and the control is then absent rather than
   * disabled: there is nothing for a hint to explain, because the whole surface is a read.
   */
  onAmend?: () => void;
}

/**
 * §14b's Amendment History — every move of this hearing's time or place, oldest first.
 *
 * §12a's Service History shape, down to its classes, because it is the same kind of record and
 * the case file has no reason to say one of them in a second way. Three parts to a line: what
 * moved, why, and who and when.
 *
 * ABSENT ENTIRELY when the case file carries no `amendments` field, which is what an API older
 * than migration 068 sends — a heading promising that the reason and the old time are kept,
 * over an empty list, would be a promise nothing behind it keeps.
 *
 * Every typed value sits in its own `<bdi>`, as the service history's do: the reason and the
 * venue are what somebody typed, and a right-to-left name must be able to reorder nothing but
 * itself.
 */
function AmendmentHistory({
  hearing,
}: {
  hearing: HearingCaseDetailView;
}): React.JSX.Element | null {
  const amendments = hearing.amendments ?? [];
  if (amendments.length === 0) return null;

  return (
    <div className="svchistory" data-testid="amendmentHistory">
      <span className="svchistory__head">{AMEND_HISTORY_HEADING}</span>
      <ol className="svchistory__list">
        {amendments.map((amendment, index) => {
          const line = amendmentHistoryLine(amendment);
          const note = amendmentLetterNoteOnLine(line);
          return (
            <li
              key={`${amendment.at}-${String(index)}`}
              className="svchistory__item"
              data-testid="amendmentHistoryItem"
            >
              <span className="svchistory__line">
                <span className="svchistory__what">
                  Moved from {line.fromAt} (at <bdi>{line.fromPlace}</bdi>) to {line.toAt} (at{" "}
                  <bdi>{line.toPlace}</bdi>)
                </span>{" "}
                &mdash; <bdi className="svchistory__reason">{line.reason}</bdi> &mdash;{" "}
                <span className="svchistory__who">
                  <bdi>{line.who}</bdi>, {line.when}
                </span>
              </span>
              {note === null ? null : (
                <span className="svchistory__what" data-testid="amendmentLetterWasOut">
                  {note}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * §14b's entry point: the button, and the sentence it carries when it cannot be pressed.
 *
 * Disabled with a reason rather than hidden. The case where the control has vanished is
 * precisely the case where the Technical Delegate wants to know why, and `ServiceCapture`'s
 * `Record Service` settled that: *"a real disabled button plus a hint — deliberately not an
 * absent control"*.
 */
function AmendControl({
  minutesState,
  onAmend,
}: {
  minutesState: HearingDocumentState;
  onAmend: () => void;
}): React.JSX.Element {
  const blocked = amendDisabledReason(minutesState);
  return (
    <div className="kvcell">
      <span className="kicker">Amend</span>
      <button
        type="button"
        className="gbtn gbtn--sm"
        disabled={blocked !== null}
        title={blocked ?? undefined}
        onClick={onAmend}
        data-testid="amendHearingOpen"
      >
        {AMEND_TITLE}
      </button>
      {blocked === null ? null : (
        <span className="btnhint" data-testid="amendHearingDisabled">
          {blocked}
        </span>
      )}
    </div>
  );
}

export function CaseFilePanel({
  hearing,
  clocks = null,
  onChanged,
  onAmend,
}: CaseFilePanelProps): React.JSX.Element {
  const level = hearing === null ? null : caseLevel(hearing.charges);

  return (
    <section
      className="panel panel--d1"
      aria-labelledby="caseFileTitle"
      style={{ marginTop: 18 }}
      data-howto="case-file"
    >
      <div className="panel__head">
        <h3 className="panel__title" id="caseFileTitle">
          Case File
        </h3>
        <span className="panel__note" data-testid="caseFileNote">
          {hearing === null ? (
            "—"
          ) : (
            <>
              {caseFileNoteParts(hearing).caseNumber} ·{" "}
              {hearing.teamCode === null ? null : (
                <>
                  <CountryFlag code={hearing.teamCode} /> ·{" "}
                </>
              )}
              {caseFileNoteParts(hearing).rest}
            </>
          )}
        </span>
        <span className="spacer" />
        {hearing === null ? (
          <span className="statuspill statuspill--muted" data-testid="caseFileStage">
            <span className="sdot" aria-hidden="true" />—
          </span>
        ) : (
          <span
            className="statuspill"
            title={HEARING_STAGE_TOOLTIPS[hearing.stage]}
            data-testid="caseFileStage"
          >
            <span className="sdot" aria-hidden="true" />
            {HEARING_STAGE_LABELS[hearing.stage]}
          </span>
        )}
      </div>

      {hearing === null ? (
        <div className="emptystate" data-testid="caseFileEmpty">
          <b>No Case Is Open</b>
          Click any case in the register above to open its file here.
        </div>
      ) : (
        <>
          <div className="casehead" data-testid="caseHead">
            <div className="kvcell">
              <span className="kicker">Case Number</span>
              <b>{hearing.caseNumber}</b>
            </div>
            <div className="kvcell">
              <span className="kicker">Persons Involved</span>
              {hearing.personsInvolved.map((person) => (
                <span key={`${person.name}:${person.role}`} className="hperson hperson--stacked">
                  {/* The country first — it is the first thing the TD looks for. */}
                  {hearing.teamCode === null ? null : (
                    <CountryFlag code={hearing.teamCode} className="hperson__flag" />
                  )}
                  <b className="hperson__name">{person.name}</b>
                  <span className="hperson__role">· {personRoleShort(person)}</span>
                  {/*
                   * RULING §K. A name typed in by hand is not the same fact as a name picked
                   * off the team list, and the record says which — so a reader can tell a
                   * name checked against TMS from one written down at a table on a match day.
                   */}
                  {person.enteredByHand === true ? (
                    <span className="hperson__hand" data-testid="person-by-hand">
                      Typed in by hand
                    </span>
                  ) : null}
                </span>
              ))}
            </div>
            <div className="kvcell">
              <span className="kicker">Team Manager</span>
              {(hearing.teamManagerName ?? null) === null ? (
                onChanged === undefined ? (
                  <b className="thin">Not named</b>
                ) : (
                  <TeamManagerField caseId={hearing.id} place="caseFile" onSaved={onChanged} />
                )
              ) : (
                <b>{hearing.teamManagerName}</b>
              )}
            </div>
            <div className="kvcell">
              <span className="kicker">Match Reference</span>
              <b>{matchRefText(hearing)}</b>
            </div>
            <div className="kvcell">
              <span className="kicker">Time Of The Match</span>
              <b>{matchTimeText(hearing)}</b>
            </div>
            <div className="kvcell">
              <span className="kicker">Charges</span>
              {hearing.charges.map((charge) => (
                <span key={charge.article} className="tbschip">
                  Art {charge.article}
                </span>
              ))}
            </div>
            <div className="kvcell">
              <span className="kicker">Minimum Penalty</span>
              <b title={level === null ? undefined : minimumPenaltyTitle(level)}>
                {level === null ? "—" : HEARING_LEVEL_MINIMUM_PENALTY[level]}
              </b>
            </div>
          </div>

          {/*
           * §14b — THE ARRANGEMENTS, AND THE ONE CONTROL THAT MOVES THEM.
           *
           * The two facts and the act that changes them, together. Before this the case file
           * showed the hearing's time only inside step 03's caption (`Set For …`) and its place
           * nowhere at all — so the Technical Delegate who had moved a hearing could not see,
           * on the case he was looking at, what the record still believed.
           *
           * The control is DISABLED with a reason rather than hidden once the hearing has been
           * held: the case that most needs an explanation is the one where the button has gone,
           * and `ServiceCapture` settled this idiom for the same reason.
           */}
          <div className="casehead" data-testid="hearingArrangements">
            <div className="kvcell">
              <span className="kicker">Time Of The Hearing</span>
              <b>{formatInstant(hearing.hearingAt) ?? "Not set"}</b>
            </div>
            <div className="kvcell">
              <span className="kicker">Place Of The Hearing</span>
              <b>{hearing.hearingLocation ?? "Not set"}</b>
            </div>
            {onAmend === undefined ? null : (
              <AmendControl
                minutesState={hearing.documents[2]}
                onAmend={onAmend}
              />
            )}
          </div>

          <AmendmentHistory hearing={hearing} />

          <StepList hearing={hearing} />

          {/* Step card 01 — the only card of the five whose record this slice writes. */}
          <div
            className={`warnnote ${hearing.isLateReport ? "warnnote--hard" : "warnnote--advisory"}`}
            role="note"
            data-testid="lateBanner"
          >
            <span className="warnnote__kind">
              {hearing.isLateReport ? "Late Report" : "Within 24 Hours"}
            </span>
            <span className="warnnote__msg">
              {hearing.isLateReport ? (
                <>
                  <b>
                    The report reached the TD more than 24 hours after the conduct and was
                    accepted at the TD&rsquo;s discretion.
                  </b>{" "}
                  Reasons stated: {hearing.lateReasons.join("; ") || "none recorded"}
                </>
              ) : (
                <>
                  <b>The report reached the TD within 24 hours of the conduct.</b> No exercise
                  of discretion was needed.
                </>
              )}
            </span>
            {/* §15's first clock, beside the rule it measures. Small and factual: the banner
                above states the discretion, and this states the window it was measured
                against — counted from the incident, and only while no report has arrived. */}
            <LateReportChip
              incidentAt={hearing.incidentAt}
              reportReceivedAt={hearing.reportReceivedAt}
              thresholdHours={clocks?.lateReportThresholdHours ?? null}
            />
          </div>

          <div className="casehead" data-testid="caseCreated">
            <div className="kvcell">
              <span className="kicker">Created By</span>
              <b>{hearing.createdBy}</b>
            </div>
            <div className="kvcell">
              <span className="kicker">Reported By</span>
              <b>{hearing.reportedBy}</b>
            </div>
            <div className="kvcell">
              <span className="kicker">Report Received</span>
              <b>{formatInstant(hearing.reportReceivedAt) ?? "—"}</b>
            </div>
            <div className="kvcell">
              <span className="kicker">Time Of The Match</span>
              <b>{matchTimeText(hearing)}</b>
            </div>
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <span className="field__label">Incident Details</span>
            <p className="previewbox">{hearing.incidentDetails}</p>
          </div>

          <h4 className="kicker" style={{ marginTop: 16 }}>
            Charges — Article Wording As Bound To This Tournament
          </h4>
          {hearing.charges.map((charge, index) => (
            <div className="previewbox" key={charge.article} data-testid={`charge-${charge.article}`}>
              <b>
                Charge {index + 1} · Article {charge.article} · Level {charge.level}
              </b>
              <p>{charge.articleText}</p>
              <p className="thin">
                <b>Minimum penalty:</b> {HEARING_LEVEL_MINIMUM_PENALTY[charge.level]} ·
                Seriousness within the level: {charge.seriousness}
              </p>
            </div>
          ))}

          <div className="panel__foot" data-testid="caseFileNotBuilt">
            <b>Not built yet, so keep these on paper for now.</b> There is no natural-justice
            checklist, no attendee list and no in-absentia wording; no decision record — whether
            the breach was accepted, disciplinary history, attitude at the hearing, any penalty
            already incurred, the finding, the reasoning and the sentence; no penalty definition
            and no tracking of suspensions served; and nowhere to attach evidence files. Your
            own notes on the case are stored but cannot be edited here.{" "}
            <b>Both clocks are working.</b> The 24-hour report window is the chip beside the
            rule above; the appeal window is on the Decision Letter&rsquo;s card in the
            Documents panel, because it runs from the moment that letter was handed over and
            from nothing else.
          </div>
        </>
      )}
    </section>
  );
}
